import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment-timezone'
import { useField, useFormikContext } from 'formik'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
  paymentType as paymentTypeConst,
  useErrorService,
} from '@abroad/components'
import {
  ExperienceCheckbox,
  ExperienceCheckboxField,
  ExperienceDatePicker,
  ExperienceSelect,
} from '../form'
import { getConditionalSessionLabel } from '../coachingPlan/schedulingHelpers'
import API from '../../utils/API'

const { ONETIME, RECURRING } = paymentTypeConst

export const QUEST_SOW_TYPE = 'QUEST'
export const REGULAR_SOW_TYPE = 'REGULAR'
export const MAX_CONDITIONAL_SESSIONS = 3

export const isManualScheduling = (value) =>
  value === false || value === 'false'

// Same defaults the network form applies when the SOW type becomes Experience.
export const questSOWDefaults = {
  isPaymentRequired: 'false',
  paymentCycle: ONETIME,
  onetimeAmount: 0,
  totalDurationOfContract: '1',
  sessionPerCoachingCycle: 2,
  numberOfCoachingCycle: 1,
  typeOfCoachingCycle: 'Yearly',
  totalSession: 2,
  isConditionalSchedulingEnabled: true,
  includeGrowthSession: true,
  sessionDuration: 60,
  growthSessionDuration: 60,
  coachingRate: '',
  growthCoachingRate: '',
}

const conditionOptions = [
  { value: 'on_or_before', label: 'On or Before' },
  { value: 'on_or_after', label: 'On or After' },
  { value: 'between', label: 'Between' },
]

export const SOWSection = ({ title, children }) => (
  <div className='experience-sec'>
    <div className='experience-sec-head'>
      <h3>{title}</h3>
    </div>
    <div className='experience-sec-body'>{children}</div>
  </div>
)

// Label on the left, control(s) on the right — same positions as the network SOW form.
export const SOWRow = ({ label, htmlFor, children }) => (
  <div className='experience-sow-row'>
    {label ? (
      <label className='experience-f-label' htmlFor={htmlFor}>
        {label}
      </label>
    ) : (
      <span />
    )}
    <div className='experience-sow-row-fields'>{children}</div>
  </div>
)

export const InfoTip = ({ id, children }) => (
  <OverlayTrigger
    placement='top'
    overlay={
      <Tooltip id={id} className='experience-sow-tooltip'>
        {children}
      </Tooltip>
    }>
    <span className='experience-sow-info' aria-label='More information'>
      i
    </span>
  </OverlayTrigger>
)

const SOWRadio = ({
  name,
  value,
  label,
  description,
  disabled,
  checked,
  onSelect,
  info,
}) => (
  <label
    className={`experience-sow-radio${disabled ? ' is-disabled' : ''}`}
    aria-disabled={disabled}>
    <input
      type='radio'
      name={name}
      value={value}
      checked={checked}
      disabled={disabled}
      onChange={() => onSelect(value)}
    />
    <b>{label}</b>
    {description ? <span>{description}</span> : null}
    {info}
  </label>
)

export const SchedulingTypeRadios = ({
  disabled,
  canUsePlatformScheduling,
  unavailableMessage,
}) => {
  const [field, , helpers] = useField('isPlatformScheduling')
  const isManual = isManualScheduling(field.value)
  return (
    <div className='experience-sow-radios' role='radiogroup'>
      <SOWRadio
        name={field.name}
        value='true'
        label='Platform Scheduling'
        checked={!isManual}
        disabled={disabled || !canUsePlatformScheduling}
        onSelect={helpers.setValue}
        info={
          !canUsePlatformScheduling && unavailableMessage ? (
            <InfoTip id='quest-sow-platform-tip'>{unavailableMessage}</InfoTip>
          ) : null
        }
      />
      <SOWRadio
        name={field.name}
        value='false'
        label='Manual Scheduling'
        checked={isManual}
        disabled={disabled}
        onSelect={helpers.setValue}
      />
    </div>
  )
}

export const SOWTypeRadios = ({ disabled }) => {
  const { values } = useFormikContext()
  const [field, , helpers] = useField('sowType')
  const isManual = isManualScheduling(values.isPlatformScheduling)
  return (
    <div className='experience-sow-radios' role='radiogroup'>
      <SOWRadio
        name={field.name}
        value={REGULAR_SOW_TYPE}
        label='REGULAR'
        checked={field.value === REGULAR_SOW_TYPE}
        disabled={disabled}
        onSelect={helpers.setValue}
      />
      <SOWRadio
        name={field.name}
        value={QUEST_SOW_TYPE}
        label='Experience'
        checked={field.value === QUEST_SOW_TYPE}
        disabled={disabled || isManual}
        onSelect={helpers.setValue}
        info={
          isManual ? (
            <InfoTip id='quest-sow-type-tip'>
              Experience type SOW is only available with Platform Scheduling.
            </InfoTip>
          ) : null
        }
      />
    </div>
  )
}

export const ExperienceQuestSelect = ({
  disabled,
  showErrorMessage,
  currentOption,
  cachedOptions,
  onOptionsLoaded,
}) => {
  const ErrorService = useErrorService()
  const hasCache = Array.isArray(cachedOptions) && cachedOptions.length > 0
  const [options, setOptions] = useState(hasCache ? cachedOptions : [])
  const [isLoading, setIsLoading] = useState(!hasCache)

  useEffect(() => {
    if (hasCache) return undefined
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await API.AdminAPI.quests.getQuestList({
          page: 1,
          limit: 500,
          isArchived: false,
        })
        if (cancelled) return
        const questOptions = (data || []).map((quest) => ({
          value: quest.questId || quest._id,
          label: quest.name || quest.title,
        }))
        setOptions(questOptions)
        if (onOptionsLoaded) onOptionsLoaded(questOptions)
      } catch (e) {
        if (!cancelled) ErrorService.showError(e)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasCurrent =
    !currentOption?.value ||
    !currentOption?.label ||
    options.some((option) => option.value === currentOption.value)

  return (
    <ExperienceSelect
      name='questId'
      id='quest-sow-questId'
      options={hasCurrent ? options : [currentOption, ...options]}
      placeholder={isLoading ? 'Loading Experiences…' : 'Select Experience'}
      disabled={disabled || isLoading}
      showErrorMessage={showErrorMessage}
    />
  )
}

export const getConditionalSchedulingDisabledReason = (values) => {
  if (isManualScheduling(values.isPlatformScheduling)) {
    return 'Conditional Scheduling is only available with Platform Scheduling.'
  }
  if (values.sowType === QUEST_SOW_TYPE) {
    return 'Conditional Scheduling is always enabled for Experience type SOW.'
  }
  const total = Number(values.totalSession)
  if (!total || total > MAX_CONDITIONAL_SESSIONS) {
    return 'Conditional Scheduling is only available for 3 or fewer sessions.'
  }
  return ''
}

export const ConditionalSchedulingCheckbox = ({ disabled }) => {
  const { values } = useFormikContext()
  const reason = getConditionalSchedulingDisabledReason(values)
  const isLockedOn =
    values.sowType === QUEST_SOW_TYPE &&
    !isManualScheduling(values.isPlatformScheduling)

  // Experience SOWs always use conditional scheduling: shown as an active,
  // checked box that can't be unchecked.
  if (isLockedOn) {
    return (
      <span className='experience-sow-check is-locked' title={reason}>
        <ExperienceCheckbox
          name='isConditionalSchedulingEnabled'
          label='Conditional Scheduling'
          checked
          disabled={disabled}
          onChange={() => {}}
        />
      </span>
    )
  }

  return (
    <span className='experience-sow-check' title={reason || undefined}>
      <ExperienceCheckboxField
        name='isConditionalSchedulingEnabled'
        label='Conditional Scheduling'
        disabled={disabled || !!reason}
      />
      {isManualScheduling(values.isPlatformScheduling) ? (
        <InfoTip id='quest-sow-conditional-tip'>{reason}</InfoTip>
      ) : null}
    </span>
  )
}

const canConfigureConditions = (values) => {
  const total = Number(values.totalSession)
  return (
    !!values.isConditionalSchedulingEnabled &&
    total > 0 &&
    total <= MAX_CONDITIONAL_SESSIONS
  )
}

export const SchedulingConditionsSection = ({ disabled, showErrors }) => {
  const { values, errors } = useFormikContext()
  const total = Number(values.totalSession) || 0

  if (!canConfigureConditions(values)) {
    let message =
      'Please configure total sessions to enable scheduling conditions.'
    if (!values.isConditionalSchedulingEnabled) {
      message =
        'Please enable Conditional Scheduling to configure session scheduling conditions.'
    } else if (!total || total > MAX_CONDITIONAL_SESSIONS) {
      message =
        'Conditional Scheduling is only available for plans with 3 or fewer sessions.'
    }
    return <p className='experience-f-hint experience-sow-muted'>{message}</p>
  }

  const listError =
    showErrors && typeof errors.schedulingConditions === 'string'
      ? errors.schedulingConditions
      : null

  return (
    <>
      {listError ? (
        <p className='experience-f-error experience-sow-list-error'>
          {listError}
        </p>
      ) : null}
      {Array.from({ length: total }, (_, index) => {
        const sessionIndex = index + 1
        const base = `schedulingConditions[${index}]`
        const condition = values.schedulingConditions?.[index] || {}
        return (
          <div className='experience-sow-session' key={sessionIndex}>
            <h4>{getConditionalSessionLabel(sessionIndex, values.sowType)}</h4>
            <SOWRow label='Condition' htmlFor={`quest-sow-condition-${index}`}>
              <ExperienceSelect
                name={`${base}.condition`}
                id={`quest-sow-condition-${index}`}
                options={conditionOptions}
                placeholder='Select Condition'
                isSearchable={false}
                disabled={disabled}
                showErrorMessage={showErrors}
              />
            </SOWRow>
            <SOWRow label='Start Date'>
              <div className='experience-f'>
                <ExperienceDatePicker
                  name={`${base}.primaryDate`}
                  showErrorMessage={showErrors}
                  disabled={disabled}
                  className='experience-f-control'
                  position='up'
                  minimumDate={moment().subtract(1, 'years')}
                  maximumDate={moment().add(1, 'years')}
                />
              </div>
            </SOWRow>
            {condition.condition === 'between' ? (
              <SOWRow label='End Date'>
                <div className='experience-f'>
                  <ExperienceDatePicker
                    key={`end-${sessionIndex}-${condition.primaryDate || ''}`}
                    name={`${base}.secondaryDate`}
                    showErrorMessage={showErrors}
                    disabled={disabled}
                    className='experience-f-control'
                    position='up'
                    minimumDate={
                      condition.primaryDate
                        ? moment(condition.primaryDate, 'MM/DD/YYYY')
                        : moment().subtract(1, 'years')
                    }
                    maximumDate={moment().add(1, 'years')}
                  />
                </div>
              </SOWRow>
            ) : null}
          </div>
        )
      })}
    </>
  )
}

/**
 * Keeps scheduling-related fields consistent, mirroring the network SOW form:
 * coach availability, platform/manual switch, Experience defaults and the
 * per-session scheduling conditions list.
 */
export const SOWSchedulingEffects = ({
  values,
  setFieldValue,
  notificationService,
  canUsePlatformScheduling,
  defaultQuestId,
}) => {
  const { setFieldTouched } = useFormikContext()
  const prevIsManual = useRef(isManualScheduling(values.isPlatformScheduling))
  const prevSowType = useRef(values.sowType)
  const prevSowCopy = useRef(values.sowCopy?.value)
  const prevTotalSession = useRef(values.totalSession)

  useEffect(() => {
    if (
      !canUsePlatformScheduling &&
      !isManualScheduling(values.isPlatformScheduling)
    ) {
      setFieldValue('isPlatformScheduling', 'false')
    }
  }, [canUsePlatformScheduling, values.isPlatformScheduling, setFieldValue])

  useEffect(() => {
    const isManual = isManualScheduling(values.isPlatformScheduling)
    if (isManual && !prevIsManual.current) {
      if (values.sowType === QUEST_SOW_TYPE) {
        setFieldValue('sowType', REGULAR_SOW_TYPE)
        notificationService.showNotification(
          'Experience type SOW is only available with Platform Scheduling.',
          'danger',
        )
        if (values.isPaymentRequired === 'false') {
          setFieldValue('isPaymentRequired', 'true')
        }
        if (values.paymentCycle === ONETIME) {
          setFieldValue('paymentCycle', RECURRING)
        }
        if (String(values.onetimeAmount) === '0') {
          setFieldValue('onetimeAmount', '')
        }
        if (values.totalDurationOfContract === '1') {
          setFieldValue('totalDurationOfContract', '')
        }
        if (String(values.sessionPerCoachingCycle) === '2') {
          setFieldValue('sessionPerCoachingCycle', '')
        }
        if (String(values.numberOfCoachingCycle) === '1') {
          setFieldValue('numberOfCoachingCycle', '')
        }
        if (values.typeOfCoachingCycle === 'Yearly') {
          setFieldValue('typeOfCoachingCycle', 'Monthly')
        }
        if (String(values.totalSession) === '2') {
          setFieldValue('totalSession', '')
        }
        if (String(values.sessionDuration) === '60') {
          setFieldValue('sessionDuration', '')
        }
        if (String(values.growthSessionDuration) === '60') {
          setFieldValue('growthSessionDuration', '')
        }
        if (values.includeGrowthSession) {
          setFieldValue('includeGrowthSession', false)
        }
        if (values.growthCoachingRate) {
          setFieldValue('growthCoachingRate', '')
        }
      }
      if (values.questId) {
        setFieldValue('questId', '')
      }
      if (values.isConditionalSchedulingEnabled) {
        setFieldValue('isConditionalSchedulingEnabled', false)
        setFieldValue('schedulingConditions', [])
      }
    }
    prevIsManual.current = isManual
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.isPlatformScheduling])

  useEffect(() => {
    const copyValue = values.sowCopy?.value
    const isCopyOperation = !!copyValue && copyValue !== prevSowCopy.current
    const isQuest = values.sowType === QUEST_SOW_TYPE

    if (isQuest && prevSowType.current !== QUEST_SOW_TYPE && !isCopyOperation) {
      Object.entries(questSOWDefaults).forEach(([key, value]) => {
        if (values[key] !== value) setFieldValue(key, value)
      })
    }

    if (isQuest) {
      // Surface the "max 3 sessions" error on the field right away instead
      // of waiting for Save.
      if (
        Number(values.totalSession) > MAX_CONDITIONAL_SESSIONS &&
        prevTotalSession.current !== values.totalSession
      ) {
        setFieldTouched('totalSession', true, true)
      }
      if (!values.isConditionalSchedulingEnabled) {
        setFieldValue('isConditionalSchedulingEnabled', true)
      }
    }

    prevSowType.current = values.sowType
    prevSowCopy.current = copyValue
    prevTotalSession.current = values.totalSession
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.sowType,
    values.sowCopy,
    values.totalSession,
    values.isConditionalSchedulingEnabled,
  ])

  useEffect(() => {
    const total = Number(values.totalSession) || 0
    const isEnabled = !!values.isConditionalSchedulingEnabled
    const isQuest = values.sowType === QUEST_SOW_TYPE
    const current = Array.isArray(values.schedulingConditions)
      ? values.schedulingConditions
      : []

    if (!isQuest && total > MAX_CONDITIONAL_SESSIONS && isEnabled) {
      setFieldValue('isConditionalSchedulingEnabled', false)
      if (current.length > 0) setFieldValue('schedulingConditions', [])
      return
    }

    if (isEnabled && total > 0 && total <= MAX_CONDITIONAL_SESSIONS) {
      if (current.length !== total) {
        setFieldValue(
          'schedulingConditions',
          Array.from({ length: total }, (_, i) => ({
            condition: '',
            primaryDate: '',
            secondaryDate: '',
            ...(current[i] || {}),
            sessionIndex: i + 1,
          })),
        )
      }
      return
    }

    if (!isEnabled && !isQuest && current.length > 0) {
      setFieldValue('schedulingConditions', [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.totalSession,
    values.isConditionalSchedulingEnabled,
    values.sowType,
    values.schedulingConditions,
  ])

  // An Experience SOW defaults to the experience being built, e.g. after a
  // manual-scheduling round trip or copying a regular SOW cleared it.
  useEffect(() => {
    if (
      defaultQuestId &&
      values.sowType === QUEST_SOW_TYPE &&
      !isManualScheduling(values.isPlatformScheduling) &&
      !values.questId
    ) {
      setFieldValue('questId', defaultQuestId)
    }
  }, [
    defaultQuestId,
    values.sowType,
    values.isPlatformScheduling,
    values.questId,
    setFieldValue,
  ])

  // "between" is the only condition with an end date.
  useEffect(() => {
    ;(values.schedulingConditions || []).forEach((item, index) => {
      if (
        item?.condition &&
        item.condition !== 'between' &&
        item.secondaryDate
      ) {
        setFieldValue(`schedulingConditions[${index}].secondaryDate`, '')
      }
    })
  }, [values.schedulingConditions, setFieldValue])

  return null
}
